// The room registry and the save-diff pair that operates over it.
//
// The 11 rooms were TypeScript builders until 2026-07-31 (Phase 2) -- functions
// that computed their tilemaps in loops and returned an object literal. They are
// authored JSON now, loaded through `load.ts`, because a world editor has to
// write rooms and a room cannot be both code in one place and data in another
// without the two diverging. The exporter that produced these files, and the test
// that checked its output against the builders byte for byte, both lived in the
// two commits either side of this one; the builders themselves are in the history
// if one ever needs consulting.
//
// The function shape is unchanged on purpose: `buildAllRooms()` and each
// `buildRoomN()` still return a fresh, independent RoomDef per call, so nothing
// that calls them -- the game, WorldState, the tests -- had to change. What used
// to be "run the builder" is now "clone and check the authored data".
import { RoomDef, RoomMutation } from '../types';
import { loadRoom } from './load';
import room0 from './data/room0.json';
import room1 from './data/room1.json';
import room2 from './data/room2.json';
import room3 from './data/room3.json';
import room4 from './data/room4.json';
import room5 from './data/room5.json';
import room6 from './data/room6.json';
import room7 from './data/room7.json';
import room8 from './data/room8.json';
import room9 from './data/room9.json';
import room10 from './data/room10.json';
import room11 from './data/room11.json';
import room12 from './data/room12.json';
import room13 from './data/room13.json';
import room14 from './data/room14.json';
import room15 from './data/room15.json';
import room16 from './data/room16.json';

// Static imports, one line per room, rather than a glob: `import.meta.glob` is a
// Vite feature, and the sim is deliberately importable by anything that can run
// TypeScript, not only by a bundler. Seventeen explicit lines also means a room
// file that nobody imports is visibly not part of the world.
export const AUTHORED_ROOMS: Record<string, unknown> = {
  room0,
  room1,
  room2,
  room3,
  room4,
  room5,
  room6,
  room7,
  room8,
  room9,
  room10,
  room11,
  room12,
  room13,
  room14,
  room15,
  room16,
};

export const ROOM_BUILDERS: Record<string, () => RoomDef> = Object.fromEntries(
  Object.entries(AUTHORED_ROOMS).map(([id, authored]) => [id, () => loadRoom(authored)]),
);

// Named accessors, kept from the builder era so unit tests and the editor's room
// picker can take one specific room without indexing the registry by string.
export const buildRoom0 = (): RoomDef => loadRoom(room0);
export const buildRoom1 = (): RoomDef => loadRoom(room1);
export const buildRoom2 = (): RoomDef => loadRoom(room2);
export const buildRoom3 = (): RoomDef => loadRoom(room3);
export const buildRoom4 = (): RoomDef => loadRoom(room4);
export const buildRoom5 = (): RoomDef => loadRoom(room5);
export const buildRoom6 = (): RoomDef => loadRoom(room6);
export const buildRoom7 = (): RoomDef => loadRoom(room7);
export const buildRoom8 = (): RoomDef => loadRoom(room8);
export const buildRoom9 = (): RoomDef => loadRoom(room9);
export const buildRoom10 = (): RoomDef => loadRoom(room10);
export const buildRoom11 = (): RoomDef => loadRoom(room11);
export const buildRoom12 = (): RoomDef => loadRoom(room12);
export const buildRoom13 = (): RoomDef => loadRoom(room13);
export const buildRoom14 = (): RoomDef => loadRoom(room14);
export const buildRoom15 = (): RoomDef => loadRoom(room15);
export const buildRoom16 = (): RoomDef => loadRoom(room16);

export function buildAllRooms(): Record<string, RoomDef> {
  const rooms = Object.fromEntries(Object.entries(ROOM_BUILDERS).map(([id, build]) => [id, build()]));
  // Cross-room references, which one room's own load cannot check: a door naming
  // a room that does not exist strands the player at a doorway that silently
  // refuses to open. Checked here, where the whole world is in hand, and cheaply
  // enough to leave on -- twelve rooms, a couple of doors each.
  for (const room of Object.values(rooms)) {
    for (const door of room.doors) {
      if (!rooms[door.toRoom]) throw new Error(`world JSON: ${room.id}: the door at (${door.x},${door.y}) leads to "${door.toRoom}", which is not a room`);
    }
  }
  return rooms;
}

// Diffs each room's current objects against a fresh build of the same room: which
// object ids are gone (melted/removed) and which remaining ones have `properties`
// worth restoring. This is the only way to capture "world" progress (melted ice,
// drained braziers) since it isn't tracked anywhere else -- room.objects arrays are
// mutated in place as the player plays.
//
// Note that this diffs against the *authored* world, so re-authoring a room in the
// editor invalidates saves taken against the old one. That is a deliberate,
// declared cost of Phase 2 (see design/09_Roadmap.md): pre-launch save
// compatibility is disposable, and WORLD_FORMAT_VERSION is the lever if it ever
// stops being.
export function serializeRoomMutations(rooms: Record<string, RoomDef>): Record<string, RoomMutation> {
  const result: Record<string, RoomMutation> = {};
  for (const [roomId, room] of Object.entries(rooms)) {
    const canonical = ROOM_BUILDERS[roomId]?.();
    if (!canonical) continue;
    const currentIds = new Set(room.objects.map((o) => o.id));
    const removedObjectIds = canonical.objects.map((o) => o.id).filter((id) => !currentIds.has(id));
    // Whole objects, and *every* respect in which one can differ (2026-08-12).
    //
    // This used to record `properties` alone, which lost two whole categories of
    // progress. Anything a mechanism keeps in its own cluster -- `gate.open`,
    // `barrier.transformed`, `water.frozen`, `sand.stage`, `heatState.current`,
    // and every live bind link -- was simply dropped. And an object created at
    // runtime could not be represented at all, which was the worse half: a
    // `Separate`·`Mass` split removes the authored boulder and adds two pieces,
    // so a save taken afterwards recorded the boulder as removed and the pieces
    // as `properties` attached to ids nothing would rebuild. Loading deleted all
    // three. In room7, whose pressure pads need specific mass tiers, that made
    // the room unsolvable from a legitimate save. Reported in
    // `review/2026-08-07-architecture-review.md` §4.6 by reading, never
    // reproduced there; `sim.test.ts` now reproduces it and pins the fix.
    //
    // Compared by serialization rather than field by field so that a cluster
    // added later is covered without anything being taught about it. The
    // comparison is conservative in the safe direction: a false *difference*
    // costs a few bytes in the save, while a false *match* would lose progress,
    // and only the former is reachable (any real change alters the string).
    const byId = new Map(canonical.objects.map((o) => [o.id, JSON.stringify(o)]));
    const changedObjects = room.objects.filter((o) => byId.get(o.id) !== JSON.stringify(o)).map((o) => structuredClone(o));
    result[roomId] = { removedObjectIds, changedObjects };
  }
  return result;
}

// Applies a saved diff on top of a freshly-built room set (in place).
export function applyRoomMutations(rooms: Record<string, RoomDef>, mutations: Record<string, RoomMutation>): void {
  for (const [roomId, room] of Object.entries(rooms)) {
    const mutation = mutations[roomId];
    if (!mutation) continue;
    room.objects = room.objects.filter((o) => !mutation.removedObjectIds.includes(o.id));
    if (mutation.changedObjects) {
      for (const saved of mutation.changedObjects) {
        const index = room.objects.findIndex((o) => o.id === saved.id);
        const restored = structuredClone(saved);
        // Replaced wholesale rather than merged: the saved object *is* the live
        // one, so a cluster the player's actions removed has to stay removed.
        // An id with no counterpart in the rebuilt room is one that only ever
        // existed at runtime, and is appended -- this is the case the old shape
        // could not express.
        if (index >= 0) room.objects[index] = restored;
        else room.objects.push(restored);
      }
    } else if (mutation.propertyOverrides) {
      // A save written before 2026-08-12. It carries `properties` and nothing
      // else, so this restores exactly what that shape was able to capture and
      // no more -- runtime-created objects in such a save are unrecoverable,
      // because the information was never written down.
      for (const obj of room.objects) {
        const override = mutation.propertyOverrides[obj.id];
        if (override) obj.properties = { ...override };
      }
    }
  }
}
